import React, { useEffect, useState, useRef } from "react";
import "./StudentMessagePage.css";
import { get_messages_for_user } from "../../../../api/messages";
import { Link } from "react-router";
import { parent_routes } from "../../../router/all_routes";
import { getFileAttachementByEntityId } from "../../../../api/fileAttachement";
import {
  Action,
  buildPermissionContext,
  Resource,
} from "../../../../core/common/autorisations/permissionsMap";

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserProfile {
  firstname?: string;
  lastname?: string;
  email?: string;
  avatar?: string;
}

interface Attachment {
  id: string | number;
  filename: string;
  url?: string;
}

interface Message {
  id: string | number;
  message: string;
  message_type: "Urgent" | "Avertissement" | "Info" | string;
  createdAt: string;
  unread?: boolean;
  UserProfil?: UserProfile;
  attachments?: Attachment[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (mins < 1) return "À l'instant";
  if (mins < 60) return `Il y a ${mins} min`;
  if (hours < 24) return `Il y a ${hours}h`;
  if (days === 1) return "Hier";
  return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
}

function formatFullDate(iso: string): string {
  return new Date(iso).toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getInitials(profile?: UserProfile): string {
  const f = profile?.firstname?.[0] ?? "";
  const l = profile?.lastname?.[0] ?? "";
  return (f + l).toUpperCase() || "?";
}

function getFullName(profile?: UserProfile): string {
  return (
    `${profile?.firstname ?? ""} ${profile?.lastname ?? ""}`.trim() || "Inconnu"
  );
}

function getAvatarClass(profile?: UserProfile): string {
  const name = getFullName(profile);
  const classes = [
    "avatar-blue",
    "avatar-green",
    "avatar-red",
    "avatar-purple",
    "avatar-orange",
    "avatar-teal",
  ];
  const index = name.charCodeAt(0) % classes.length;
  return classes[index];
}

function getBadgeClass(type: string): "urgent" | "warning" | "info" {
  if (type === "Urgent") return "urgent";
  if (type === "Avertissement") return "warning";
  return "info";
}

// ─── Components ───────────────────────────────────────────────────────────────

function SearchInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="search-container">
      <svg
        className="search-icon"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <circle cx="11" cy="11" r="8" strokeWidth="2" />
        <path d="m21 21-4.35-4.35" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Rechercher un message..."
        className="search-input"
      />
      {value && (
        <button onClick={() => onChange("")} className="search-clear-btn">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              d="M18 6 6 18M6 6l12 12"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
}

function TypeBadge({ type }: { type: string }) {
  const badgeClass = getBadgeClass(type);
  return (
    <span className={`type-badge ${badgeClass}`}>
      <span className="type-badge-dot" />
      {type}
    </span>
  );
}

function MessageListItem({
  msg,
  selected,
  onClick,
}: {
  msg: Message;
  selected: boolean;
  onClick: () => void;
}) {
  const avatarClass = getAvatarClass(msg.UserProfil);

  return (
    <button
      onClick={onClick}
      className={`message-list-item ${selected ? "selected" : ""}`}
    >
      <div className="message-avatar-wrapper">
        <div className={`message-avatar ${avatarClass}`}>
          {getInitials(msg.UserProfil)}
        </div>
        {msg.unread && <span className="unread-dot" />}
      </div>

      <div className="message-item-content">
        <div className="message-item-header">
          <span className={`message-item-name ${msg.unread ? "unread" : ""}`}>
            {getFullName(msg.UserProfil)}
          </span>
          <span className="message-item-time">{formatDate(msg.createdAt)}</span>
        </div>
        <p className={`message-item-preview ${msg.unread ? "unread" : ""}`}>
          {msg.message}
        </p>
        <TypeBadge type={msg.message_type} />
      </div>
    </button>
  );
}

function MessageDetail({
  msg,
  onClose,
}: {
  msg: Message;
  onClose: () => void;
}) {
  const avatarClass = getAvatarClass(msg.UserProfil);

  return (
    <>
      <div className="detail-header">
        <button onClick={onClose} className="detail-back-btn">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              d="M15 18 9 12l6-6"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div className={`detail-avatar ${avatarClass}`}>
          {getInitials(msg.UserProfil)}
        </div>
        <div className="detail-info">
          <h3 className="detail-name">{getFullName(msg.UserProfil)}</h3>
          <p className="detail-email">{msg.UserProfil?.email}</p>
        </div>
        <div className="detail-badge">
          <TypeBadge type={msg.message_type} />
        </div>
      </div>

      <div className="detail-body">
        <div className="detail-date">
          <svg
            className="detail-date-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <rect
              x="3"
              y="4"
              width="18"
              height="18"
              rx="2"
              ry="2"
              strokeWidth="2"
            />
            <line
              x1="16"
              y1="2"
              x2="16"
              y2="6"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <line
              x1="8"
              y1="2"
              x2="8"
              y2="6"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <line x1="3" y1="10" x2="21" y2="10" strokeWidth="2" />
          </svg>
          <span>{formatFullDate(msg.createdAt)}</span>
        </div>

        <div className="message-bubble">
          <p className="message-bubble-text">{msg.message}</p>
        </div>

        {msg.attachments && msg.attachments.length > 0 && (
          <div className="attachments-section">
            <h4 className="attachments-title">
              Fichiers joints ({msg.attachments.length})
            </h4>
            <div className="attachments-list">
              {msg.attachments.map((f) => (
                <a
                  key={f.id}
                  href={f.url ?? "#"}
                  target="_blank"
                  rel="noreferrer"
                  className="attachment-item"
                >
                  <div className="attachment-icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                        strokeWidth="2"
                      />
                      <polyline points="14 2 14 8 20 8" strokeWidth="2" />
                    </svg>
                  </div>
                  <span className="attachment-name">{f.filename}</span>
                  <svg
                    className="attachment-download"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <polyline
                      points="7 10 12 15 17 10"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <line
                      x1="12"
                      y1="15"
                      x2="12"
                      y2="3"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function EmptyState({ searched }: { searched: boolean }) {
  return (
    <div className="empty-state">
      <div className="empty-state-icon">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
            strokeWidth="1.5"
          />
        </svg>
      </div>
      <p className="empty-state-title">
        {searched ? "Aucun message trouvé" : "Aucun message"}
      </p>
      {searched && (
        <p className="empty-state-description">Essayez avec d'autres termes</p>
      )}
    </div>
  );
}

function NoSelection() {
  return (
    <div className="empty-state no-selection">
      <div className="empty-state-icon">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
            strokeWidth="1.5"
          />
        </svg>
      </div>
      <h3 className="empty-state-title">Sélectionnez un message</h3>
      <p className="empty-state-description">
        Choisissez une conversation dans la liste pour afficher son contenu ici.
      </p>
    </div>
  );
}

function SkeletonItem() {
  return (
    <div className="skeleton-item">
      <div className="skeleton-avatar" />
      <div className="skeleton-content" style={{ flex: 1 }}>
        <div className="skeleton-line" />
        <div className="skeleton-line short" />
        <div className="skeleton-line short" style={{ width: "50%" }} />
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const MessagePage: React.FC = () => {
  const routes = parent_routes;
  const [messages, setMessages] = useState<Message[]>([]);
  const [filtered, setFiltered] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const [selected, setSelected] = useState<Message | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const unreadCount = messages.filter((m) => m.unread).length;

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await get_messages_for_user();
      const data = Array.isArray(res?.data) ? res.data : [];
      const sorted = data.sort(
        (a: any, b: any) =>
          new Date(b?.createdAt || 0).getTime() -
          new Date(a?.createdAt || 0).getTime(),
      );

      const responses = await Promise.all(
        sorted.map(async (message: any) => {
          const res = await getFileAttachementByEntityId(
            message.id,
            "Message",
            buildPermissionContext(Resource.MESSAGE, Action.READ),
          );
          const messageFiles = Array.isArray(res.data) ? res.data : [];
          return {
            ...message,
            attachments: messageFiles.map((f: any) => ({
              id: f.id,
              filename: f.fileOriginalName,
              url: f.temporaryUrl,
            })),
          };
        }),
      );
      // console.log("Mes messages: ", responses);
      setMessages(responses);
      setFiltered(responses);
      // console.log("Mes messages: ", data);
      setMessages(responses);
      setFiltered(responses);
    } catch (e) {
      setMessages([]);
      setFiltered([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchMessages();
  }, []);

  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      setFiltered(messages);
      setPage(1);
      return;
    }
    setFiltered(
      messages.filter((m) => {
        const name = getFullName(m.UserProfil).toLowerCase();
        return (
          name.includes(q) ||
          (m.UserProfil?.email ?? "").toLowerCase().includes(q) ||
          m.message.toLowerCase().includes(q) ||
          m.message_type.toLowerCase().includes(q)
        );
      }),
    );
    setPage(1);
  }, [query, messages]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageData = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSelect = (msg: Message) => {
    setSelected(msg);
    setMessages((prev) =>
      prev.map((m) => (m.id === msg.id ? { ...m, unread: false } : m)),
    );
  };

  const listSectionHidden = selected ? " hidden" : "";
  const detailSectionHidden = !selected ? " hidden" : "";

  return (
    <div className="page-wrapper student-message-page">
      <div className="content student-message-container">
        <div className="student-message-header">
          <h1>Messages</h1>
          <div className="breadcrumb">
            <span>
              <Link to={routes.parentDashboard}>Accueil</Link>
            </span>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-item">Messages</span>
          </div>
          <div className="page-title-section">
            <div className="page-title">
              {unreadCount > 0 && (
                <span className="unread-badge">{unreadCount}</span>
              )}
            </div>
            <span className="message-count">
              {messages.length} message{messages.length > 1 ? "s" : ""}
            </span>
          </div>
        </div>
        <div className="message-panel">
          <div className={`message-list-section${listSectionHidden}`}>
            <div className="list-header">
              <SearchInput value={query} onChange={setQuery} />
            </div>

            <div className="filter-chips">
              {(
                [
                  { label: "Tous", value: "" },
                  { label: "Urgent", value: "Urgent" },
                  { label: "Avertissement", value: "Avertissement" },
                  { label: "Information", value: "Info" },
                ] as const
              ).map((f) => (
                <button
                  key={f.label}
                  onClick={() => setQuery(f.value)}
                  className={`filter-chip ${
                    (f.label === "Tous" ? query === "" : query === f.value)
                      ? "active"
                      : "inactive"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div ref={listRef} className="message-list">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <SkeletonItem key={i} />
                ))
              ) : pageData.length === 0 ? (
                <EmptyState searched={query.length > 0} />
              ) : (
                pageData.map((msg) => (
                  <MessageListItem
                    key={msg.id}
                    msg={msg}
                    selected={selected?.id === msg.id}
                    onClick={() => handleSelect(msg)}
                  />
                ))
              )}
            </div>

            {totalPages > 1 && (
              <div className="pagination-footer">
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="pagination-btn"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      d="M15 18 9 12l6-6"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <span className="pagination-info">
                  {page} / {totalPages}
                </span>
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="pagination-btn"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      d="M9 18l6-6-6-6"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>

          <div className={`message-detail-section${detailSectionHidden}`}>
            {selected ? (
              <MessageDetail msg={selected} onClose={() => setSelected(null)} />
            ) : (
              <NoSelection />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagePage;
